import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "desktop",
  title: "Install Mavori for desktop",
  navLabel: "Desktop app",
  description:
    "Download and install Mavori on Windows, Apple Silicon Mac, or Linux. Find system requirements, sign-in help, and update instructions.",
  section: "start",
  published: "2026-09-23",
  updated: "2026-09-23",
  related: ["getting-started", "account", "agent"],
  blocks: [
    {
      type: "lead",
      text: "The desktop app uses the same Mavori account, conversations, and portfolios as the web app. Start at [Download Mavori](/download), choose your platform, then follow the instructions below. An internet connection is required.",
    },
    { type: "h2", text: "System requirements" },
    {
      type: "table",
      headers: ["Platform", "Requirements", "Download"],
      rows: [
        ["Windows", "Windows 10 or later, Intel / AMD x64", "EXE installer"],
        ["Mac", "macOS 12 Monterey or later, Apple Silicon (M-series)", "DMG disk image"],
        ["Linux", "Intel / AMD x64, AppImage-compatible distribution", "AppImage"],
      ],
    },
    {
      type: "p",
      text: "The Mac download does not support Intel processors. Check **About This Mac** before downloading; browser detection cannot reliably tell an Intel Mac from Apple Silicon. Windows ARM and Linux ARM do not have native Mavori installers. Use [Mavori in your browser](https://app.mavori.ai/) on other devices.",
    },
    {
      type: "p",
      text: "The bundled Electron runtime lists Ubuntu 18.04+, Fedora 32+, and Debian 10+ in its [platform requirements](https://github.com/electron/electron/blob/v42.9.1/README.md#platform-support). These describe upstream runtime compatibility, not a guarantee that every distribution has been tested with Mavori. AppImage support, including FUSE where required, depends on your distribution.",
    },
    { type: "h2", text: "Install on Windows" },
    {
      type: "ol",
      items: [
        "Choose the Windows x64 download on the [download page](/download). Our direct installers are hosted in the official **Anyrainel/mavori-desktop** GitHub release repository.",
        "Open the EXE installer and follow its installation steps. Then launch Mavori from the Start menu.",
        "Sign in with your existing account, or create one. A Google, Apple, or GitHub sign-in opens your browser; allow the browser to return to Mavori when prompted.",
      ],
    },
    {
      type: "callout",
      variant: "info",
      title: "Windows publisher warning",
      text: "The current direct installer is unsigned. Windows may show an unknown-publisher or SmartScreen warning. Check that the download came from the official release link above. Do not disable Windows security protections or work around your organization's device policy. If installation is blocked or you are unsure, continue in the [web app](https://app.mavori.ai/) and [contact support](/contact).",
    },
    { type: "h2", text: "Install on Mac" },
    {
      type: "ol",
      items: [
        "Download the Apple Silicon DMG and open it.",
        "Drag Mavori into Applications, then open Mavori from Applications. Eject the disk image after installation.",
        "Sign in to the same account you use on the web. If macOS reports that the download is damaged or cannot be verified, download it again from the official page or contact support; do not disable Gatekeeper.",
      ],
    },
    { type: "h2", text: "Install on Linux" },
    {
      type: "ol",
      items: [
        "Download the x64 AppImage and move it into a permanent folder you own.",
        "In your file manager's permissions, allow the AppImage to run as a program. Open it to launch Mavori.",
        "If your distribution reports missing FUSE support, follow your distribution's AppImage setup instructions. Use the web app if the required runtime is unavailable. Do not run Mavori as root or disable its sandbox.",
      ],
    },
    { type: "h2", text: "Sign in and continue your work" },
    {
      type: "p",
      text: "Use the same sign-in method and account as on the web to see your existing conversations and portfolios. If browser sign-in completes but the app does not reopen, return to Mavori and try again. If your browser or desktop cannot open the return link, use email sign-in or continue in the web app and contact support. Creating a second account will not bring over the first account's work.",
    },
    {
      type: "p",
      text: "Enable supported notifications in Mavori and your operating system. Notification delivery depends on permissions and whether Mavori is running; installing the app alone does not enable every alert.",
    },
    { type: "h3", text: "Updates and troubleshooting" },
    {
      type: "p",
      text: "Direct installations check for updates automatically. Use **Help → Check for Updates** to check manually, and restart when the app says an update is ready. Keep the Linux AppImage in a writable folder. If an update fails, reopen Mavori and retry or download the current installer from the official download page.",
    },
    {
      type: "p",
      text: "For help, [contact support](/contact) with your operating system, Mavori version from the About menu, and the exact error message. Do not include passwords, sign-in links, or brokerage credentials. You can keep working in the web app while installation is being resolved.",
    },
  ],
};
